// How one step of an assistant turn is shown (server: services/omni-agent/workLoop.js).
//
// The founder watched the chat and said it "reads and updates at the same
// time, neither it checks back its work" (2026-09-25). Part of that was real
// and fixed on the server; part was this screen: every step was one flat chip,
// and every finished step got a check mark, which read as "checked" when it
// only meant "the tool ran". So:
//   - a read looks like a read (an eye), not a tick;
//   - a change gets a tick only when the server read it back and it landed,
//     and says "checked";
//   - a change that did not land says so, in the error colour;
//   - a step the work loop refused (read first, ask first) is not shown;
//   - a plan is shown as the list of steps it is.
export const DONE_LABEL = {
  save_answer: 'Answer saved', confirm_answer: 'Answer confirmed', save_story_draft: 'Story drafted', pin_draft_answer: 'Draft pinned',
  approve_draft_answer: 'Draft approved', draft_cover_letter: 'Letter drafted', save_job: 'Job saved',
  mark_applied: 'Marked applied', queue_job: 'Application queued', cancel_run: 'Run stopped',
  create_resume_variant: 'Version created', update_resume_entry: 'Change proposed', add_resume_entry: 'Change proposed',
  delete_resume_entry: 'Change proposed', batch_update_resume: 'Changes proposed', update_design_settings: 'Design updated',
  arrange_resume_sections: 'Change proposed',
};
const WRITE_KINDS = new Set(['proposal', 'change', 'final']);

/** The steps worth showing, in order: not refused, not the plan itself. */
export const visibleSteps = (tools = []) => tools.filter(t => t && t.status !== 'refused' && !t.refused && t.name !== 'plan_steps');

/** The plan's steps, when the turn made one. */
export const planOf = (tools = []) => {
  const plan = tools.find(t => t?.name === 'plan_steps' && Array.isArray(t.input?.steps));
  return plan ? plan.input.steps.slice(0, 8) : null;
};

/**
 * { state, icon, text, tone } for one step. `label` is the running label
 * ("Saving Your Answer"); state is running / failed / missed / checked / read / done.
 */
export function stepView(tool, label) {
  if (tool.status === 'running') return { state: 'running', icon: null, text: label, tone: 'running' };
  if (tool.status === 'failed') return { state: 'failed', icon: 'close', text: `${label} didn't go through`, tone: 'failed' };
  if (tool.verified === false) return { state: 'missed', icon: 'error', text: `${DONE_LABEL[tool.name] || label}: didn't land`, tone: 'failed' };
  if (WRITE_KINDS.has(tool.kind) && tool.verified === true) return { state: 'checked', icon: 'check_circle', text: `${DONE_LABEL[tool.name] || label} · checked`, tone: 'checked' };
  if (tool.kind === 'read') return { state: 'read', icon: 'visibility', text: label, tone: '' };
  return { state: 'done', icon: 'check', text: label, tone: '' };
}

// What the model is doing between steps (server 'status' events). A long
// tool call being written used to show nothing at all — 44 seconds of a
// frozen-looking chat (founder, 2026-09-27) — so the step it is writing is
// named, with a clock, until the step itself or the reply arrives.
const WRITING_LABEL = {
  batch_update_resume: 'Writing resume changes', update_resume_entry: 'Writing a resume change',
  add_resume_entry: 'Writing a resume change', delete_resume_entry: 'Preparing a resume change',
  create_resume_variant: 'Making a new version', update_design_settings: 'Changing the design',
  save_answer: 'Writing an answer', save_story_draft: 'Writing a story', pin_draft_answer: 'Drafting an answer', approve_draft_answer: 'Approving',
  draft_cover_letter: 'Writing a cover letter', queue_job: 'Starting the application',
  search_jobs: 'Searching jobs', plan_steps: 'Planning', navigate: 'Opening the page',
  arrange_resume_sections: 'Arranging your sections',
};

/**
 * The live line's words when the server sends none (a server from before
 * chat-2): 'Thinking', the step being written or run, the reply, or its
 * check. Null when nothing is live.
 */
export function liveLabel(live) {
  if (!live) return null;
  // 'writing' with a tool is a step being written (before chat-2); without
  // one it is the reply (chat-2's phases: thinking, working, writing, checking).
  if (live.phase === 'writing') return live.tool ? (WRITING_LABEL[live.tool] || 'Working') : 'Writing the reply';
  if (live.phase === 'working') return WRITING_LABEL[live.tool] || 'Working';
  if (live.phase === 'replying') return 'Writing the reply';
  if (live.phase === 'checking') return 'Checking the reply';
  return 'Thinking';
}

/**
 * The one status line while a turn works (chat-2, 2026-10-30): the server's
 * own words when it sends them ("Searching 889,000 jobs…"), else the step's
 * name (`toolLabel`, the sidebar's) while one runs, else liveLabel's words.
 */
export function statusLabel(live, toolLabel = null) {
  if (!live) return null;
  if (typeof live.label === 'string' && live.label.trim()) return live.label.trim();
  if (live.phase === 'working' && toolLabel) return toolLabel;
  return liveLabel(live);
}

/** A turn's clock: "8s" under a minute, "1:05" from a minute on. */
export function turnClock(ms) {
  const s = Math.max(0, Math.round((ms || 0) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
